import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Factory, LogIn, Loader2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username.trim(), password);
      toast.success("Bienvenido");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background hero-gradient">
      {/* Left visual */}
      <div className="hidden lg:flex relative">
        <img
          src="https://images.pexels.com/photos/4484154/pexels-photo-4484154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Operación de almacén"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Factory className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">OT Maquila</div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Sistema de órdenes de trabajo</div>
            </div>
          </div>
          <div className="max-w-md">
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-[420px] bg-card border-border shadow-[0_18px_40px_hsl(0_0%_0%/0.55)]">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 lg:hidden">
              <Factory className="h-5 w-5 text-primary" />
              <span className="text-base font-semibold">OT Maquila</span>
            </div>
            <CardTitle className="text-2xl font-semibold tracking-tight">Iniciar sesión</CardTitle>
            <CardDescription className="text-sm text-muted-foreground">
              Accede con tu usuario y contraseña. Tu admin te asignó un rol.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-xs uppercase tracking-wide text-muted-foreground">Usuario</Label>
                <Input
                  id="username"
                  data-testid="login-username-input"
                  autoComplete="username"
                  placeholder="admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-xs uppercase tracking-wide text-muted-foreground">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="login-toggle-password"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full h-10">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
                Entrar
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                ¿No tienes cuenta?{" "}
                <Link to="/register" data-testid="register-link" className="text-primary hover:underline">Regístrate</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
